import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { ServiceOrderProduct } from 'contracts/service-order-products'
import { Formatter } from 'utils'
import cortevaLogo from '../../../../../../assets/images/corteva-logo.png'
import webcolLogo from '../../../../../../assets/images/logo-webcol.png'

pdfMake.vfs = pdfFonts.vfs

export const getPrintLogoBase64 = async (): Promise<string> => {
  const img = new Image()
  img.src = cortevaLogo
  await img.decode()

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = img.width
  canvas.height = img.height
  ctx.drawImage(img, 0, 0)

  return canvas.toDataURL()
}

export const getWebcolLogo = async (): Promise<string> => {
  const img = new Image()
  img.src = webcolLogo
  await img.decode()

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = img.width
  canvas.height = img.height
  ctx.drawImage(img, 0, 0)

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]

    if (r > 200 && g > 200 && b > 200) {
      data[i] = 0       // R - PRETO
      data[i + 1] = 0   // G - PRETO
      data[i + 2] = 0   // B - PRETO
    }
  }

  ctx.putImageData(imageData, 0, 0)

  return canvas.toDataURL()
}

export const generateServiceOrderPDF = async (
  id: number,
  products: ServiceOrderProduct[],
  serviceOrderData?: any,
): Promise<void> => {
  const cortevaLogo = await getPrintLogoBase64()
  const webcolLogo = await getWebcolLogo()
  const docDefinition = createPDFDefinition(id, products, cortevaLogo, webcolLogo, serviceOrderData)
  pdfMake.createPdf(docDefinition).download(`OrdemDeServico_${id}.pdf`)
}

const createPDFDefinition = (
  id: number,
  products: ServiceOrderProduct[],
  cortevaLogo: string,
  webcolLogo: string,
  serviceOrderData?: any,
) => {
  const currentDate = new Date().toLocaleDateString('pt-BR')
  const observation =
    serviceOrderData?.observation ||
    serviceOrderData?.observations?.[0]?.observation ||
    'SEM DADOS'
  const task = serviceOrderData?.tasks?.[0]?.task || 'SEM DADOS'
  const responsible = serviceOrderData?.user?.name || 'SEM DADOS'

  const productRows = products.map((p) => [
    {
      text: `${p.material || 'SEM DADOS'}\n${p.materialDescription || 'SEM DADOS'}`,
      fontSize: 7,
    },
    { text: `${Formatter.decimal(p.blockedStock)}`, fontSize: 7, alignment: 'center' },
    { text: `${p.unit || ''}`, fontSize: 7, alignment: 'center' },
    {
      text: Formatter.date(p.shelfLifeExpirationDate) || 'SEM DADOS',
      fontSize: 7,
      alignment: 'center',
    },
    { text: `${p.priority || ''}`, fontSize: 7, alignment: 'center' },
    { text: p.user?.name || 'SEM DADOS', fontSize: 7, alignment: 'center' },
    { text: '', fontSize: 7, alignment: 'center' },
  ])

  return {
    pageSize: 'A4',
    pageMargins: [20, 100, 20, 100],

    header: () => [
      {
        columns: [
          cortevaLogo
            ? {
                image: cortevaLogo,
                width: 90,
                alignment: 'left',
                margin: [20, 15, 0, 0],
              }
            : { text: '' },
          {
            text: `ORDEM DE SERVIÇO ${String(id).padStart(4, '0')}`,
            style: 'title',
            alignment: 'center',
            margin: [0, 50, 40, 0],
            width: '*',
          },
          webcolLogo
            ? {
                image: webcolLogo,
                width: 65,
                alignment: 'right',
                margin: [0, 15, 20, 0],
              }
            : { text: '' },
        ],
        columnGap: 10,
      },
    ],

    content: [
      {
        table: {
          widths: ['*'],
          body: [[{ text: 'INFORMAÇÕES GERAIS', style: 'sectionHeader', border: [false, false, false, false], margin: [0, 4, 0, 4] }]],
        },
        layout: { fillColor: () => '#003366' },
        margin: [0, 0, 0, 8],
      },
      {
        columns: [
          {
            stack: [
              { text: 'NÚMERO DA OS', style: 'label' },
              { text: String(id).padStart(4, '0'), style: 'value' },
              { text: '\n' },
              { text: 'TIPO DE SERVIÇO', style: 'label' },
              { text: task, style: 'value', margin: [0, 2, 0, 12] },
              { text: 'OBSERVAÇÃO', style: 'label' },
              { text: observation, style: 'value', margin: [0, 2, 0, 12] },
            ],
            width: '50%',
            margin: [5, 0, 0, 0],
          },
          {
            stack: [
              { text: 'DATA DE CRIAÇÃO', style: 'label' },
              { text: currentDate, style: 'value' },
              { text: '\n' },
              { text: 'RESPONSÁVEL', style: 'label' },
              { text: responsible, style: 'value', margin: [0, 2, 0, 12] },
            ],
            width: '50%',
            margin: [5, 0, 0, 0],
          },
        ],
        margin: [0, 0, 0, 20],
      },
      {
        table: {
          widths: ['*'],
          body: [[{ text: 'PRODUTOS E SERVIÇOS', style: 'sectionHeader', border: [false, false, false, false], margin: [0, 4, 0, 4] }]],
        },
        layout: { fillColor: () => '#003366' },
        margin: [0, 0, 0, 8],
      },
      {
        table: {
          headerRows: 1,
          widths: ['34%', '12%', '8%', '12%', '10%', '14%', '10%'],
          body: [
            [
              { text: 'CÓDIGO E DESCRIÇÃO', style: 'tableHeader' },
              { text: 'QUANTIDADE', style: 'tableHeader' },
              { text: 'VOLUME', style: 'tableHeader' },
              { text: 'DATA VENC.', style: 'tableHeader' },
              { text: 'PRIORIDADE', style: 'tableHeader' },
              { text: 'RESPONSÁVEL', style: 'tableHeader' },
              { text: 'QTD TRABALHOS', style: 'tableHeader' },
            ],
            ...productRows,
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 1 : 0.5),
          vLineWidth: () => 0.5,
          hLineColor: (i: number) => (i === 0 ? '#003366' : '#aaaaaa'),
          vLineColor: () => '#aaaaaa',
          fillColor: (rowIndex: number) => (rowIndex === 0 ? '#003366' : null),
        },
        margin: [0, 0, 0, 20],
      },
      {
        table: {
          widths: ['*'],
          body: [[{ text: 'NOMES DOS PARTICIPANTES', style: 'sectionHeader', border: [false, false, false, false], margin: [0, 4, 0, 4] }]],
        },
        layout: { fillColor: () => '#003366' },
        margin: [0, 0, 0, 8],
      },
      {
        table: {
          widths: ['*'],
          body: Array.from({ length: 5 }).map((_, i, arr) => [
            {
              canvas: [{ type: 'line', x1: 15, y1: 20, x2: 535, y2: 20, lineWidth: 0.5 }],
              border: [true, i === 0, true, i === arr.length - 1],
              margin: [0, 10, 0, i === arr.length - 1 ? 15 : 0],
            },
          ]),
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#aaaaaa',
          vLineColor: () => '#aaaaaa',
          paddingTop: () => 0,
          paddingBottom: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 0,
        },
        margin: [0, 0, 0, 20],
      },
    ],

    footer: () => [
      {
        stack: [
          {
            text: '__________________________________________________________',
            alignment: 'center',
            fontSize: 10,
            margin: [0, 0, 0, 5],
          },
          { text: 'ASSINATURA', alignment: 'center', style: 'signatureLabel' },
        ],
        alignment: 'center',
        margin: [0, 40, 0, 20],
      },
    ],

    styles: {
      title: { fontSize: 16, bold: true, color: '#000000' },
      sectionHeader: { fontSize: 10, bold: true, color: 'white', alignment: 'left' },
      tableHeader: { bold: true, fontSize: 7, color: 'white', alignment: 'center' },
      label: { fontSize: 8, bold: true, color: '#666666' },
      value: { fontSize: 9, margin: [0, 0, 0, 8] },
      signatureLabel: { fontSize: 9, bold: true },
    },

    defaultStyle: { fontSize: 8 },
  }
}
